import { service } from '@ember/service'
import Component from '@glimmer/component'
import { t, type IntlService } from 'ember-intl'
import { colors } from '@colors'
import { reportingToolTip } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { AxisLabelsFormatterContextObject, Point } from 'highcharts'

export interface DrivingTestsTotalMsDataItem {
  driving_test_grade: number
  summary: {
    completed_count: number
    student_count: number
    student_average: Nullable<number>
  }
  categories: {
    category: (typeof orderedCategories.maths)[number]
    completed: number
    percent_complete: number
    total_attempts: number
  }[]
}

export interface DrivingTestsTotalReDataItem {
  summary: {
    completed_count: number
    student_count: number
    student_average: Nullable<number>
  }
  categories: {
    category: (typeof orderedCategories.re)[number]
    completed: number
    percent_complete: number
    total_attempts: number
  }[]
}

interface Args {
  data: DrivingTestsTotalReDataItem | DrivingTestsTotalMsDataItem
  product: 're' | 'maths'
  summaryTitle: string
  title: string
  tooltip: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const orderedCategories = {
  maths: ['number', 'operations', 'patterns', 'geometry', 'data', 'measurement'],
  re: ['sight_words', 'letters_and_sounds', 'content_words'],
} as const satisfies Record<'re' | 'maths', string[]>

export class DrivingTestsTotals extends Component<Signature> {
  @service intl!: IntlService

  /**
   * By setting this to false, along with no set maximum `yRange` means that when a student has 100%, the y axis ticks
   * don't go beyond 100%.
   */
  readonly yAxisEndOnTick = false
  readonly yRange: [number, null] = [0, null]

  get categories() {
    return orderedCategories[this.args.product].map((category) => {
      switch (category) {
        case 'data':
          return this.intl.t('driving_tests.maths.categories.data')
        case 'geometry':
          return this.intl.t('driving_tests.maths.categories.geometry')
        case 'measurement':
          return this.intl.t('driving_tests.maths.categories.measurement')
        case 'number':
          return this.intl.t('driving_tests.maths.categories.number')
        case 'operations':
          return this.intl.t('driving_tests.maths.categories.operations')
        case 'patterns':
          return this.intl.t('driving_tests.maths.categories.patterns')
        case 'content_words':
          return this.intl.t('driving_tests.re.categories.content_words')
        case 'letters_and_sounds':
          return this.intl.t('driving_tests.re.categories.letters_and_sounds')
        case 'sight_words':
          return this.intl.t('driving_tests.re.categories.sight_words')
        default:
          return ''
      }
    })
  }

  get colors() {
    const { product } = this.args
    return orderedCategories[product].map((category) => {
      switch (`${product}.${category}`) {
        case 'maths.geometry':
          return colors.yolkyYellow300
        case 'maths.data':
          return colors.watermelonyRed300
        case 'maths.measurement':
          return colors.wlBlue250
        case 'maths.number':
        case 're.sight_words':
          return colors.oceanyBlue300
        case 'maths.operations':
        case 're.letters_and_sounds':
          return colors.forestGreen300
        case 'maths.patterns':
        case 're.content_words':
          return colors.purplyPink300
        default:
          return colors.oceanyBlue300
      }
    })
  }

  get colorScheme() {
    if (this.args.product === 're') return SummaryBoxColorScheme.DarkBlue
    return SummaryBoxColorScheme.LightBlue
  }

  get chartData(): SeriesColumnOptions[] {
    return [
      {
        data: orderedCategories[this.args.product].map((category) => {
          return this.args.data.categories.find((c) => c.category === category)?.percent_complete ?? 0
        }),
        colorByPoint: true,
        colors: this.colors,
        type: 'column',
      },
    ]
  }

  get toolTip() {
    const { intl } = this
    const { product, data } = this.args

    return reportingToolTip('x', undefined, {
      formatter(this: Point) {
        const category = orderedCategories[product][this.x]
        const categoryData = data.categories.find((categoryData) => categoryData.category === category)
        if (!categoryData) return false

        const { completed, total_attempts } = categoryData

        const completedText = intl.t('reporting.tooltips.drivingTests.completedTests', { count: completed })
        const totalText = intl.t('reporting.tooltips.drivingTests.totalAttempts', { count: total_attempts })

        return `${completedText}<br/>${totalText}`
      },
      backgroundColor: colors.neutral400,
    })
  }

  get summaryStats() {
    const { summary } = this.args.data
    const completed = summary.completed_count
    const studentCount = summary.student_count
    const average = summary.student_average ?? 0

    return [
      { label: this.intl.t('reporting.chartTitles.dashboard.drivingTests'), value: completed.toString() },
      { label: this.intl.t('reporting.statBoxes.courseProgress.studentCount'), value: studentCount.toString() },
      { label: this.intl.t('reporting.statBoxes.courseProgress.perStudentCount'), value: average.toString() },
    ]
  }

  yAxisFormatter(this: AxisLabelsFormatterContextObject) {
    return `${this.value}%`
  }

  <template>
    <div
      data-test-giraffe-driving-tests-category
      data-test-giraffe-driving-tests-totals
      class="relative h-full"
      ...attributes
    >
      <ChartInfoTooltip @tooltipContent={{@tooltip}} />
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @title={{@title}}
            @categories={{this.categories}}
            @data={{this.chartData}}
            @yRange={{this.yRange}}
            @yAxisLabelFormatter={{this.yAxisFormatter}}
            @xAxisTickWidth={{0}}
            @yAxisLabel={{t "reporting.yAxisLabels.drivingTestsTotals"}}
            @yAxisEndOnTick={{this.yAxisEndOnTick}}
            @toolTip={{this.toolTip}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default DrivingTestsTotals
